import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ArrayConfiguration from 'pages/products/configuration/ArrayConfiguration';
import AutoCompleteDataInputTableCell from 'pages/products/configuration/AutoCompleteDataInputTableCell';
import TextInputTableCell from 'pages/products/configuration/TextInputTableCell';
import Title from 'components/ui/general/Title';
import { Field, getFormValues, reduxForm } from 'redux-form';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import utils from 'helpers/utils';
import translate from 'components/hoc/translate';
import { omit } from 'lodash';

const RULES_INITIAL_VALUES = {
    require_one_of: null,
};

class ProductRulesConfigurationContainer extends Component {

    RULE_COMPONENTS = {
        require_one_of: {
            component: ArrayConfiguration,
            inputComponent: AutoCompleteDataInputTableCell,
            inputComponentProps: {
                reference: 'products',
                searchBy: 'title',
                searchParams: {
                    related_product_id: this.props.productId,
                },
            },
            fields: [
                'title',
            ],
            type: 'array',
        },
    }

    componentDidMount() {
        this.generateInitialValues();
    }

    componentDidUpdate(prevProps) {
        const { initialValues } = this.props;

        if (!utils.isEmpty(utils.diffObjects(prevProps.initialValues, initialValues))) {
            this.generateInitialValues();
        }
    }

    generateInitialValues = () => {
        const { initialize } = this.props;

        const initialValues = {};

        utils.each(this.props.initialValues, (value, key) => {
            if (utils.isArray(value)) {
                initialValues[key] = this.formatArrayValues(value);
            } else {
                initialValues[key] = value;
            }
        });

        initialize(initialValues);
    }

    getUpdatedRulesConfiguration = () => {
        const { ruleValues } = this.props;

        const updatedRulesConfiguration = {};

        if (!utils.isEmpty(ruleValues)) {
            utils.each(ruleValues, (value, key) => {
                if (utils.isEmpty(value)) {
                    return null;
                }

                if (utils.isArray(value)) {
                    const formattedValues = utils.map(value, (valueKeyPair) => {
                        const keys = Object.keys(valueKeyPair);

                        if (keys.length > 2) {
                            return omit(valueKeyPair, 'id'); // Multi-column support, returns Array<Object>.
                        }

                        return valueKeyPair.value;
                    }).filter((value) => {
                        return !utils.isEmpty(value);
                    });

                    if (!utils.isEmpty(formattedValues)) {
                        updatedRulesConfiguration[key] = formattedValues;
                    }
                } else {
                    updatedRulesConfiguration[key] = value;
                }
            });
        }

        return updatedRulesConfiguration;
    }

    submitValues = () => {
        const { name, onSubmitChange } = this.props;

        const updatedRulesConfiguration = this.getUpdatedRulesConfiguration();

        if (utils.isEmpty(updatedRulesConfiguration)) {
            return onSubmitChange(name, null);
        }

        return onSubmitChange(name, updatedRulesConfiguration);
    }

    formatArrayValues = (values = []) => {
        return utils.map(values, (value) => {
            if (utils.isObject(value)) {
                return value;
            }

            return {
                id: Math.floor(Math.random() * Date.now()),
                value: value,
            };
        });
    }

    onAdd = (key) => {
        const { ruleValues, change } = this.props;

        const rulesCopy = [].concat(ruleValues[key]).filter((value) => {
            return value;
        });

        rulesCopy.push('');

        const updatedRules = this.formatArrayValues(rulesCopy);

        change(key, updatedRules);
    }

    onChange = (event, newValue, values, key) => {
        if (utils.isFunction(event.preventDefault)) {
            event.preventDefault();
        }

        const { id, value } = event.target;

        const { change } = this.props;

        const updatedRules = [
            ...values,
        ].map((keyValuePair) => {
            if (keyValuePair.id == id) {
                return {
                    ...keyValuePair,
                    value: value,
                };
            }

            return keyValuePair;
        });

        change(key, updatedRules);

        this.submitValues();
    }

    onDelete = (id, name, values = []) => {
        const { change, name: parentName, onSubmitChange } = this.props;

        const index = utils.findIndex(values, (keyValuePair) => {
            return keyValuePair.id === id;
        });

        const updatedRules = [
            ...values,
        ];

        if (updatedRules.length > 1) {
            updatedRules.splice(index, 1);
        } else if (updatedRules.length) {
            updatedRules.length = 0;
        }

        change(name, updatedRules);

        const updatedRulesConfiguration = this.getUpdatedRulesConfiguration();

        if (utils.isEmpty(updatedRules)) {
            updatedRulesConfiguration[name] = null;
        } else {
            updatedRulesConfiguration[name] = updatedRules.map(({ value }) => {
                return value;
            });
        }

        return onSubmitChange(parentName, updatedRulesConfiguration);
    }

    render() {
        const {
            ruleValues,
            translate,
        } = this.props;

        return (
            <div>
                <Title title={ translate('resources.products.rules') } />

                { utils.map(ruleValues, (ruleValue, key) => {
                    const ruleConfiguration = this.RULE_COMPONENTS[key] || null;

                    if (!ruleConfiguration) {
                        return null;
                    }

                    const {
                        component,
                        type,
                        ...restProps
                    } = ruleConfiguration;

                    const additionalFieldProps = { ...restProps };

                    if (type === 'array') {
                        const {
                            inputComponent: InputComponent = TextInputTableCell,
                        } = restProps;

                        additionalFieldProps.inputComponent = InputComponent;

                        additionalFieldProps.onAdd = (props) => {
                            return this.onAdd(key, props);
                        };

                        additionalFieldProps.onChange = (event, value, values) => {
                            return this.onChange(event, value, values, key);
                        };

                        additionalFieldProps.onDelete = (id, name, values = []) => {
                            return this.onDelete(id, name, values);
                        };
                    }

                    return (
                        <div key={ key }>
                            <Field
                                component={ component }
                                name={ key }
                                id={ `rule-${ key }` }
                                title={ translate(`resources.products.${ key }.title`) }
                                { ...additionalFieldProps }
                            />
                        </div>
                    );
                })}
            </div>
        );
    }
}

ProductRulesConfigurationContainer.propTypes = {
    change: PropTypes.func.isRequired,
    initialize: PropTypes.func.isRequired,
    initialValues: PropTypes.shape({
        require_one_of: PropTypes.arrayOf(PropTypes.string),
    }),
    name: PropTypes.string.isRequired,
    onSubmitChange: PropTypes.func.isRequired,
    productId: PropTypes.string.isRequired,
    ruleValues: PropTypes.shape({
        require_one_of: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.number,
            value: PropTypes.string,
        })),
    }),
    translate: PropTypes.func.isRequired,
};

const mapStateToProps = (state, props) => {
    return {
        form: 'product-rules',
        ruleValues: getFormValues('product-rules')(state),
        initialValues: props.rules || RULES_INITIAL_VALUES,
    };
};

const enhance = compose(
    translate,
    connect(mapStateToProps, null),
    reduxForm(),
);

export default enhance(ProductRulesConfigurationContainer);



// WEBPACK FOOTER //
// ./src/pages/products/configuration/rules/ProductRulesConfigurationContainer.js